import prototypesTable from "../data/tables/prototypes.json";
import { SceneKey } from "./scene-keys";

/**
 * Playable prototype registry — driven by `data/tables/prototypes.json`
 * (Unity PitchShell SSOT too).
 */
export type MobileControlsMode = "none" | "platformer";

/** Title hub section — play vertical slices vs tools/harnesses. */
export type HubSection = "play" | "tools";

export type ProtoDef = {
  id: string;
  scene: SceneKey;
  aliases?: readonly string[];
  title: string;
  showInHub?: boolean;
  hubSection?: HubSection;
  primary?: boolean;
  mobileControls?: MobileControlsMode;
  cloudflareProject?: string;
};

export type HubMeta = {
  title: string;
  subtitle: string;
  version: string;
};

type PrototypesFile = {
  hubTitle?: string;
  hubSubtitle?: string;
  hubVersion?: string;
  pagesBaseUrl?: string;
  pagesProject?: string;
  protos: Array<{
    id: string;
    scene: string;
    title?: string;
    aliases?: string[];
    showInHub?: boolean;
    hubSection?: HubSection | string;
    primary?: boolean;
    mobileControls?: MobileControlsMode;
    cloudflareProject?: string;
  }>;
};

function asSceneKey(scene: string): SceneKey {
  const values = Object.values(SceneKey) as string[];
  if (!values.includes(scene)) {
    throw new Error(
      `prototypes.json unknown scene '${scene}'. Add SceneKey first.`,
    );
  }
  return scene as SceneKey;
}

function asHubSection(raw: string | undefined): HubSection {
  return raw === "tools" ? "tools" : "play";
}

const table = prototypesTable as PrototypesFile;

export const HUB_META: HubMeta = {
  title: table.hubTitle?.trim() || "Game Proto",
  subtitle: table.hubSubtitle?.trim() || "플레이어블 프로토타입 홈",
  version: table.hubVersion?.trim() || "0.1.0",
};

export const PAGES_BASE_URL =
  table.pagesBaseUrl?.replace(/\/$/, "") ||
  "https://pocket-world-ffa.pages.dev";

export const PAGES_PROJECT = table.pagesProject || "pocket-world-ffa";

export function shareUrlFor(entryId: string): string {
  return `${PAGES_BASE_URL}/?entry=${encodeURIComponent(entryId)}`;
}

export function protoDisplayTitle(proto: ProtoDef): string {
  const t = proto.title?.trim();
  return t || proto.id;
}

export const PROTO_REGISTRY: readonly ProtoDef[] = table.protos.map((p) => {
  if (!p.id?.trim()) {
    throw new Error("prototypes.json row missing id");
  }
  const section = asHubSection(p.hubSection);
  return {
    id: p.id.trim(),
    scene: asSceneKey(p.scene),
    title: (p.title ?? p.id).trim(),
    aliases: p.aliases,
    showInHub: p.showInHub,
    hubSection: section,
    primary: p.primary,
    mobileControls: p.mobileControls === "platformer" ? "platformer" : "none",
    cloudflareProject: p.cloudflareProject,
  };
});

const byToken = new Map<string, ProtoDef>();

function indexRegistry(): void {
  if (byToken.size > 0) return;
  for (const p of PROTO_REGISTRY) {
    byToken.set(p.id.toLowerCase(), p);
    byToken.set(p.scene.toLowerCase(), p);
    for (const a of p.aliases ?? []) {
      byToken.set(a.toLowerCase(), p);
    }
  }
}

export function findProtoByToken(token: string): ProtoDef | undefined {
  indexRegistry();
  const key = token.trim().toLowerCase();
  if (!key) return undefined;
  return byToken.get(key);
}

export function findProtoByScene(scene: SceneKey): ProtoDef | undefined {
  return findProtoByToken(scene);
}

/** Playable prototypes listed under Title → Prototypes. */
export function hubProtos(): readonly ProtoDef[] {
  return PROTO_REGISTRY.filter(
    (p) => p.showInHub !== false && (p.hubSection ?? "play") === "play",
  );
}

/** Tools / harnesses listed under Title → Tools (e.g. Art Lab). */
export function toolProtos(): readonly ProtoDef[] {
  return PROTO_REGISTRY.filter((p) => (p.hubSection ?? "play") === "tools");
}

export function primaryProto(): ProtoDef | undefined {
  return PROTO_REGISTRY.find((p) => p.primary) ?? hubProtos()[0];
}

export function registeredPlayScenes(): SceneKey[] {
  const seen = new Set<SceneKey>();
  const out: SceneKey[] = [];
  for (const p of PROTO_REGISTRY) {
    if (seen.has(p.scene)) continue;
    seen.add(p.scene);
    out.push(p.scene);
  }
  return out;
}

export function mobileControlsForScene(scene: SceneKey): MobileControlsMode {
  if (scene === SceneKey.Boot || scene === SceneKey.Title) return "none";
  return findProtoByScene(scene)?.mobileControls ?? "none";
}
